import { getLogger } from '@sas/nova-core/logging';
import { isBaseThemeName } from './themeOverrides';
import { defaultThemes, ReportThemeParametersMap } from './themeParameters';
const logger = getLogger('reportThemes');

/**
 * Creates a `ThemeProvider` compatible theme from a set of `ThemeParameterOverrides`.
 *
 * NOTE: Prioritizes `baseReportThemeName` to be set to the desired report theme to work
 * properly. Falls back to `baseThemeName`.
 *
 * @param overrides `ThemeParameterOverrides`
 * @returns `ThemeParameters`
 */
export function resolveReportThemeOverrides(overrides) {
  let baseTheme;
  const reportThemeName = overrides.baseReportThemeName ?? overrides.baseThemeName;
  if (isSystemReportTheme(reportThemeName)) {
    baseTheme = getReportThemeParameters(reportThemeName);
  } else {
    // eslint-disable-next-line @sas/i18n/jsx-to-localize
    logger.warn(`Unsupported 'baseReportThemeName' provided ('${reportThemeName}'). Defaulting to 'light'.`);
    baseTheme = getReportThemeParameters('light');
  }
  return {
    ...baseTheme,
    colors: {
      ...baseTheme.colors,
      ...overrides.colors
    },
    overrides: {
      ...baseTheme.overrides,
      ...overrides.overrides
    }
  };
}

/**
 * Get the base set of `ThemeParameters` that make up the requested report theme.
 *
 * @param reportThemeName SAS supported report theme id
 * @returns
 */
export function getReportThemeParameters(reportThemeName) {
  if (!isSystemReportTheme(reportThemeName)) {
    return defaultThemes.light;
  }
  const {
    baseThemeName,
    colors
  } = ReportThemeParametersMap[reportThemeName];
  let baseAppThemeName = baseThemeName;
  if (!isBaseThemeName(baseAppThemeName)) {
    // eslint-disable-next-line @sas/i18n/jsx-to-localize
    logger.warn(`Unsupported 'baseThemeName' provided ('${baseAppThemeName}'). Defaulting to 'light'.`);
    baseAppThemeName = 'light';
  }
  const baseTheme = defaultThemes[baseAppThemeName];
  const isTheme2025Enabled = reportThemeName === 'light2025' || reportThemeName === 'dark2025' || reportThemeName === 'highcontrast2025';
  return {
    ...baseTheme,
    colors: {
      ...baseTheme.colors,
      ...colors
    },
    baseReportThemeName: reportThemeName,
    enableNovaTokens: isTheme2025Enabled
  };
}
export function isSystemReportTheme(themeName) {
  return ReportThemeParametersMap.hasOwnProperty(themeName);
}
export function isDarkReportTheme(theme) {
  const isReportTheme = !!theme.baseReportThemeName;
  const isDark = theme.baseThemeName === 'dark' || theme.baseThemeName === 'high-contrast';
  return isReportTheme && isDark;
}
export function getReportThemeColorDerivation(theme) {
  switch (theme.baseReportThemeName) {
    case 'marine':
    case 'midnight':
    case 'opal':
    case 'sail':
    case 'aqua':
    case 'umstead':
    case 'highcontrast2018':
    case 'highcontrast2017':
    case 'light':
    case 'dark':
    case 'snow':
    case 'light2025':
    case 'dark2025':
    case 'highcontrast2025':
    case 'highcontrast':
      return '2019';
    case 'midnight2020':
    case 'highcontrast2020':
    default:
      return 'latest';
  }
}
//# sourceMappingURL=reportThemes.js.map